/**
 * Detalle/edición de un aviso. Permite editar sus campos y gestionar sus
 * placements (listar + agregar). Cada placement enlaza a su propio detalle
 * (evidencias, factura, transición de estado).
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Megaphone, ChevronRight, MapPin, Radio, Trash2,
} from "lucide-react";
import { useMe } from "@/hooks/use-me";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { AdStateBadge } from "@/components/feature/media/ad-state-badge";
import { PlacementForm } from "@/components/feature/media/placement-form";
import {
  media, AD_TYPE_LABELS, type AdType, type AdPriority,
} from "@/lib/api";

export const Route = createFileRoute("/_auth/media/ads_/$adId")({
  component: AdDetail,
});

const PRIORITIES: AdPriority[] = ["baja", "normal", "alta", "urgente"];

function AdDetail() {
  const { adId } = Route.useParams();
  const id = Number(adId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const canDelete = me?.role === "owner" || me?.role === "admin" || me?.role === "superadmin";
  const adQ = useQuery({ queryKey: ["media", "ad", id], queryFn: () => media.getAd(id) });

  const isDiscarded = adQ.data?.status === "descartado";
  const toggleStatus = useMutation({
    mutationFn: () => isDiscarded ? media.reactivateAd(id) : media.discardAd(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "ad", id] });
      qc.invalidateQueries({ queryKey: ["media", "ads"] });
      toast.success(isDiscarded ? "Aviso reactivado" : "Aviso descartado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: () => media.deleteAd(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "ads"] });
      toast.success("Aviso eliminado");
      navigate({ to: "/media/ads" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (adQ.isLoading) return <div className="px-6 py-6 max-w-4xl mx-auto"><Skeleton className="h-64" /></div>;
  if (!adQ.data) return <div className="px-6 py-6">Aviso no encontrado</div>;
  const ad = adQ.data;

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      <Link to="/media/ads" className="text-xs text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Volver a avisos
      </Link>

      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
          <Megaphone size={20} className="text-brand-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-ink-900 dark:text-white">{ad.title}</h1>
            <Badge variant="outline" className="font-mono text-[10px]">{ad.code}</Badge>
            {isDiscarded && (
              <Badge className="text-[10px] bg-slate-200 text-slate-700">Descartado</Badge>
            )}
          </div>
          <CampaignLink campaignId={ad.campaign_id} />
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Button
            variant={isDiscarded ? "outline" : "destructive"}
            size="sm"
            disabled={toggleStatus.isPending}
            onClick={() => {
              if (isDiscarded || confirm(`¿Marcar el aviso "${ad.title}" como descartado? Quedará en el historial sin difundirse.`))
                toggleStatus.mutate();
            }}
          >
            {isDiscarded ? "Reactivar" : "Descartar"}
          </Button>
          {canDelete && (
            <Button
              variant="destructive"
              size="sm"
              disabled={remove.isPending}
              title="Eliminar aviso permanentemente"
              onClick={() => {
                if (confirm(`¿Eliminar el aviso "${ad.title}" de forma permanente? Esta acción no se puede deshacer.`))
                  remove.mutate();
              }}
            >
              <Trash2 size={14} /> Eliminar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2"><AdEditForm adId={id} /></div>
        <div className="lg:col-span-3"><AdPlacements adId={id} /></div>
      </div>
    </div>
  );
}

function CampaignLink({ campaignId }: { campaignId: number }) {
  const q = useQuery({ queryKey: ["media", "campaign", campaignId], queryFn: () => media.getCampaign(campaignId) });
  return (
    <Link to="/media/campaigns/$campaignId" params={{ campaignId: String(campaignId) }}
      className="text-xs text-brand-600 hover:underline mt-0.5 inline-block">
      Campaña: {q.data?.name ?? `#${campaignId}`}
    </Link>
  );
}

function AdEditForm({ adId }: { adId: number }) {
  const qc = useQueryClient();
  const adQ = useQuery({ queryKey: ["media", "ad", adId], queryFn: () => media.getAd(adId) });
  const categoriesQ = useQuery({
    queryKey: ["media", "ad-categories"],
    queryFn: () => media.listAdCategories({ is_active: true }),
  });
  const [form, setForm] = useState<{
    title: string; ad_type: AdType; category_id: string; priority: AdPriority; summary: string;
  } | null>(null);

  // Sync inicial
  if (adQ.data && form === null) {
    setForm({
      title: adQ.data.title,
      ad_type: adQ.data.ad_type as AdType,
      category_id: adQ.data.category_id ? String(adQ.data.category_id) : "",
      priority: (adQ.data.priority as AdPriority) || "normal",
      summary: adQ.data.summary ?? "",
    });
  }

  const save = useMutation({
    mutationFn: () => media.patchAd(adId, {
      title: form!.title,
      ad_type: form!.ad_type,
      category_id: form!.category_id ? Number(form!.category_id) : null,
      priority: form!.priority,
      summary: form!.summary || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "ad", adId] });
      qc.invalidateQueries({ queryKey: ["media", "ads"] });
      toast.success("Aviso actualizado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!form) return <Skeleton className="h-64" />;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Datos del aviso</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Título</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Tipo</Label>
          <Select value={form.ad_type} onValueChange={(v) => setForm({ ...form, ad_type: v as AdType })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(AD_TYPE_LABELS).map(([k, lbl]) => (
                <SelectItem key={k} value={k}>{lbl}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Categoría</Label>
          <Select value={form.category_id || "none"}
            onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Sin categoría</SelectItem>
              {(categoriesQ.data ?? []).map(c => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Prioridad</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v as AdPriority })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Resumen</Label>
          <Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          <Save size={14} /> {save.isPending ? "Guardando…" : "Guardar cambios"}
        </Button>
      </CardContent>
    </Card>
  );
}

function AdPlacements({ adId }: { adId: number }) {
  const list = useQuery({
    queryKey: ["media", "placements", "by_ad", adId],
    queryFn: () => media.listPlacements({ ad_id: adId, limit: 200 }),
  });
  const outletsQ = useQuery({
    queryKey: ["media", "outlets", "all"],
    queryFn: () => media.listOutlets({ limit: 500 }),
  });
  const [adding, setAdding] = useState(false);

  const outletName = (id: number) =>
    (outletsQ.data ?? []).find(o => o.id === id)?.name ?? `Medio #${id}`;

  const placements = list.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Placements ({placements.length})</CardTitle>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus size={14} /> Agregar placement
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <PlacementForm
            adId={adId}
            onCreated={() => setAdding(false)}
            onCancel={() => setAdding(false)}
          />
        )}

        {list.isLoading ? <Skeleton className="h-24" /> : (
          <div className="space-y-2">
            {placements.map(p => (
              <Link key={p.id} to="/media/placements/$placementId" params={{ placementId: String(p.id) }}
                className="block">
                <div className="border border-ink-200 dark:border-ink-700 rounded-lg p-3 hover:border-brand-300 hover:shadow-sm transition-all flex items-center gap-3">
                  <Radio size={16} className="text-ink-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{outletName(p.outlet_id)}</div>
                    <div className="text-xs text-ink-500 flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.coverage}</span>
                      {p.scheduled_at && <span>· {p.scheduled_at}</span>}
                      {p.cost_amount != null && <span>· Costo S/. {Number(p.cost_amount).toLocaleString("es-PE")}</span>}
                      {p.price_amount != null && <span>· Precio S/. {Number(p.price_amount).toLocaleString("es-PE")}</span>}
                    </div>
                  </div>
                  <AdStateBadge state={p.state} />
                  <ChevronRight size={16} className="text-ink-400 shrink-0" />
                </div>
              </Link>
            ))}
            {!placements.length && !adding && (
              <div className="text-center py-8 text-ink-500 text-sm border-2 border-dashed border-ink-200 rounded-lg">
                Este aviso aún no tiene placements. Agrega el primero.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
